import {Divider, Layout, Text, WarningIcon} from '@phonectas/unifon-ui-kit';
import {MrcItem} from 'common/types/store/mrcTypes';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {getBirthDate, getDateDefaultFormat} from 'utils/date';
import {MrcStyledText} from './styled';
import {MRC_FIELDS_COL_ONE, MRC_FIELDS_COL_TWO} from '../../constants';

interface MrcExpandedRowProps {
	row: MrcItem;
}

interface RowTableProps {
	rows: {
		label: string;
		value: React.ReactNode;
		formatText?: boolean;
		isDate?: boolean;
	}[];
}

const RowTable: React.FC<RowTableProps> = ({rows}) => {
	return (
		<table style={{width: '100%', borderCollapse: 'collapse'}}>
			<tbody>
				{rows.map(({label, value, formatText, isDate}, idx) => {
					const displayValue = isDate && typeof value === 'string' 
						? getBirthDate(value) 
						: value;

					return (
						<tr key={idx}>
							<td style={{padding: '4px 4px', verticalAlign: 'top', width: 160}}>
								<Text variant="body2" color="secondary">{label}:</Text>
							</td>
							<td style={{padding: '4px 4px', verticalAlign: 'top'}}>
								{
									formatText ? (
										<MrcStyledText>
											{displayValue}
										</MrcStyledText>
									) :
										<Text>
											{displayValue}
										</Text>
								}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	);
};

const MrcExpandedRow: React.FC<MrcExpandedRowProps> = ({row}) => {
	const [t] = useTranslation();
	const template = row.template || {};

	const mainRows = [
		{
			label: t('ID'),
			value: row.id || '-'
		},
		{
			label: t('Created by'),
			value: row.created_by_name || '-'
		},
		{
			label: t('SMS sent at'),
			value: getDateDefaultFormat(row.sms_sent_at) || '-'
		},
		{
			label: t('Updated at'),
			value: getDateDefaultFormat(row.updated_at) || '-'
		},
		{
			label: t('Updated by'),
			value: row?.last_change_by_name || '-'
		},
		{
			label: t('Rejected at'),
			value: getDateDefaultFormat(row.mrc_rejected_at) || '-'
		},
		{
			label: t('SMS message'),
			formatText: true,
			value: row.sms_message || '-',
		},
	];

	const templateRows = [
		{label: t('Description'), value: template.description || '-'},
		{label: t('MRC subject'), value: template.mrc_subject || '-'},
		{
			label: t('Created at'),
			value: getDateDefaultFormat(template.created_at) || '-'
		},
		{
			label: t('Updated at'),
			value: getDateDefaultFormat(template.updated_at) || '-'
		},
		{label: t('MRC message'), value: template.mrc_message || '-'},
		{label: t('Template SMS'), value: template.sms_message || '-'},
	];

	// Helper function to format values
	const formatValue = (value: unknown): string | null => {
		if (value === null || value === undefined || value === '') return null;
		if (typeof value === 'string' || typeof value === 'number') return String(value);
		if (Array.isArray(value)) return value.join(', ');
		if (typeof value === 'object') return null;
		return null;
	};

	// Filter out null/empty fields
	const colOneRows = MRC_FIELDS_COL_ONE.map(({key, label, formatText, isDate}) => ({
		label,
		value: formatValue(row?.[key as keyof MrcItem]),
		formatText,
		isDate: isDate ?? false,
	})).filter(item => item.value !== null);

	const colTwoRows = MRC_FIELDS_COL_TWO.map(({key, label, formatText, isDate}) => ({
		label,
		value: formatValue(row?.[key as keyof MrcItem]),
		formatText,
		isDate: isDate ?? false,
	})).filter(item => item.value !== null);

	return (
		<Layout fullWidth direction="column" gap="none" nowrap>
			{
				row.mrc_failed_message && (
					<Layout
						fullWidth
						nowrap
						style={{alignItems: 'flex-start'}}
					>
						<WarningIcon color="error" />
						<Text variant="h5" color="error" style={{marginBottom: 8}}>
							{row.mrc_failed_message}
						</Text>
					</Layout>
				)
			}
			<Layout fullWidth gap="none" nowrap>
				<div
					style={{
						paddingRight: 16,
						minWidth: 280,
						width: '100%',
						marginBottom: 8,
					}}
				>
					<Text variant="h5" style={{marginBottom: 8}}>
						{t('MRC details')}
					</Text>
					<RowTable rows={mainRows} />
				</div>
				{template && (
					<div
						style={{
							borderLeft: '2px solid #eee',
							paddingLeft: 16,
							minWidth: 280,
							width: '100%',
						}}
					>
						<Text variant="h5" style={{marginBottom: 8}}>
							{t('Template')}
						</Text>
						<RowTable rows={templateRows} />
					</div>
				)}
			</Layout>
			{(colOneRows.length > 0 || colTwoRows.length > 0) && (
				<Layout fullWidth direction="column" gap="xs" nowrap>
					<Divider />
					<Text variant="h5">
						{t('MRC fields')}
					</Text>
					<Layout fullWidth nowrap>
						<Layout fullWidth direction="column" nowrap>
							{colOneRows.length > 0 && (
								<Layout fullWidth>
									<RowTable rows={colOneRows} />
								</Layout>
							)}
						</Layout>
						<Layout fullWidth direction="column" nowrap>
							{colTwoRows.length > 0 && (
								<Layout fullWidth>
									<RowTable rows={colTwoRows} />
								</Layout>
							)}
						</Layout>
						<Layout fullWidth nowrap>
						</Layout>
					</Layout>
				</Layout>
			)}
			<Layout fullWidth style={{marginTop: 5}}>
				{/* <Divider /> */}
			</Layout>
		</Layout>
	);
};

export default MrcExpandedRow;

